import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  X,
  Search,
  Camera,
  QrCode,
  ChefHat,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  Loader2,
  Plus,
  Trash2,
  RotateCcw,
  BookOpen,
  ArrowLeft,
  ChevronRight,
  ShieldCheck,
  Upload,
  Layers,
  Utensils,
  Check,
  Scan,
  RefreshCw,
  Flashlight,
  StopCircle,
  MessageSquare,
} from 'lucide-react';
import { useBarcodeScanner } from '../lib/useBarcodeScanner';
import { fetchProductByBarcode, canonicalizeIngredient, synthesizeDishTitle, normalizeParsedAiIngredients } from '../lib/ingredientUtils';
import { compressImage } from '../lib/imageUtils';
import { localStore } from '../lib/storage';
import { SavedRecipe } from '../lib/types';
import {
  FodmapFoodItem,
  FodmapAnalysisResult,
  searchFodmapDatabase,
  getAllFodmapFoods,
  getFoodCategoriesList,
  getFoodsByFoodCategory,
  matchIngredientToFodmap,
  evaluateFodmapMeal,
  convertMealToLowFodmap,
  autoRegisterDiscoveredFoods,
  getRealisticCulinaryServing,
} from '../lib/fodmapEngine';
import { analyzeImageDirectOrProxy, describeMealDirectOrProxy } from '../lib/gemini';

interface FodmapScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogMeal?: (meal: { name: string; ingredients: string[] }) => void;
  initialMode?: 'search' | 'builder' | 'describe' | 'barcode' | 'photo';
}

type ScanMode = 'search' | 'builder' | 'describe' | 'barcode' | 'photo';
type RatingFilter = 'all' | 'low' | 'moderate' | 'high';

interface ScannedProductFodmapResult {
  productName: string;
  brand?: string;
  ingredients: string[];
  analysis: FodmapAnalysisResult;
  imageUrl?: string;
}

export const FodmapScannerModal: React.FC<FodmapScannerModalProps> = ({
  isOpen,
  onClose,
  onLogMeal,
  initialMode = 'search',
}) => {
  const [activeMode, setActiveMode] = useState<ScanMode>(initialMode);

  // --- SEARCH & BROWSE STATE ---
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [ratingFilter, setRatingFilter] = useState<RatingFilter>('all');
  const [selectedFood, setSelectedFood] = useState<FodmapFoodItem | null>(null);

  // --- RECIPE & PLATE BUILDER STATE ---
  const [builderTitle, setBuilderTitle] = useState('My Custom Plate');
  const [builderIngredients, setBuilderIngredients] = useState<
    Array<{
      id: string;
      name: string;
      grams: number;
      rating: 'low' | 'moderate' | 'high';
      primaryCulprit?: string;
      swapSuggestion?: string;
      safeLimitTitle?: string;
    }>
  >([]);
  const [builderSearchQuery, setBuilderSearchQuery] = useState('');
  const [builderSaveSuccess, setBuilderSaveSuccess] = useState(false);

  // --- AI DESCRIBE STATE ---
  const [describeInput, setDescribeInput] = useState('');
  const [isDescribeLoading, setIsDescribeLoading] = useState(false);
  const [describeResult, setDescribeResult] = useState<{
    title: string;
    category?: string;
    ingredients: string[];
    analysis: FodmapAnalysisResult;
    notes?: string;
  } | null>(null);
  const [describeError, setDescribeError] = useState<string | null>(null);

  // --- PHOTO SCAN STATE ---
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isPhotoScanning, setIsPhotoScanning] = useState(false);
  const [photoResult, setPhotoResult] = useState<{
    title: string;
    category?: string;
    ingredients: string[];
    analysis: FodmapAnalysisResult;
    notes?: string;
  } | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const photoCameraInputRef = useRef<HTMLInputElement>(null);
  const photoGalleryInputRef = useRef<HTMLInputElement>(null);

  // --- BARCODE SCAN STATE ---
  const [isBarcodeLoading, setIsBarcodeLoading] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeResult, setBarcodeResult] = useState<ScannedProductFodmapResult | null>(null);
  const [barcodeError, setBarcodeError] = useState<string | null>(null);
  const barcodePhotoInputRef = useRef<HTMLInputElement>(null);

  // Categories list
  const categories = useMemo(() => {
    return getFoodCategoriesList();
  }, [isOpen]);

  // Search Results
  const filteredFoods = useMemo(() => {
    let list: FodmapFoodItem[] = [];
    if (searchQuery.trim()) {
      list = searchFodmapDatabase(searchQuery.trim(), 40);
    } else if (selectedCategory !== 'all') {
      list = getFoodsByFoodCategory(selectedCategory);
    } else {
      list = getAllFodmapFoods().slice(0, 40);
    }

    if (ratingFilter !== 'all') {
      list = list.filter((f) => f.rating === ratingFilter);
    }

    return list;
  }, [searchQuery, selectedCategory, ratingFilter, isOpen]);

  // Builder Search Results
  const builderSearchResults = useMemo(() => {
    if (!builderSearchQuery.trim()) return [];
    return searchFodmapDatabase(builderSearchQuery.trim(), 8);
  }, [builderSearchQuery]);

  // Overall Plate Builder Evaluation & Stacking
  const builderEvaluation = useMemo(() => {
    if (builderIngredients.length === 0) return null;

    let highestRating: 'low' | 'moderate' | 'high' = 'low';
    const culpritCounts: Record<string, number> = {};

    for (const item of builderIngredients) {
      if (item.rating === 'high') highestRating = 'high';
      else if (item.rating === 'moderate' && highestRating !== 'high') highestRating = 'moderate';

      if (item.primaryCulprit && item.primaryCulprit !== 'None') {
        culpritCounts[item.primaryCulprit] = (culpritCounts[item.primaryCulprit] || 0) + 1;
      }
    }

    const stackedCulprits = Object.entries(culpritCounts)
      .filter(([_, count]) => count >= 2)
      .map(([culprit, count]) => ({ culprit, count }));

    return {
      overallRating: highestRating,
      stackedCulprits,
      hasStacking: stackedCulprits.length > 0,
    };
  }, [builderIngredients]);

  // --- BARCODE LOOKUP HANDLER ---
  const handleLookupBarcode = useCallback(async (code: string) => {
    const clean = code.trim();
    if (!clean || clean.length < 6) {
      setBarcodeError('Please enter a valid barcode number (6-14 digits).');
      return;
    }

    setIsBarcodeLoading(true);
    setBarcodeError(null);

    try {
      const product = await fetchProductByBarcode(clean);
      if (product) {
        const evalResult = evaluateFodmapMeal(product.ingredients, product.name);
        setBarcodeResult({
          productName: product.name,
          brand: product.brand,
          ingredients: product.ingredients,
          analysis: evalResult,
          imageUrl: product.imageUrl,
        });
        stopBarcodeCamera();
      } else {
        setBarcodeError(`Product not found for barcode "${clean}". You can upload a photo of the ingredients list or search by food name.`);
      }
    } catch (err) {
      console.warn('Barcode lookup error:', err);
      setBarcodeError('Failed to lookup barcode. Please check your network connection.');
    } finally {
      setIsBarcodeLoading(false);
    }
  }, []);

  // Shared 360° Omnidirectional Barcode Scanner Hook
  const {
    videoRef,
    isCameraActive: isBarcodeCameraActive,
    cameraError: barcodeCameraError,
    torchAvailable,
    isTorchOn,
    startCamera: startBarcodeCamera,
    stopCamera: stopBarcodeCamera,
    toggleTorch,
    decodeFromPhoto: decodeBarcodeFromPhoto,
  } = useBarcodeScanner({
    onDetected: (code) => {
      setBarcodeInput(code);
      handleLookupBarcode(code);
    },
    enabled: isOpen && activeMode === 'barcode',
  });

  // Reset state on open
  useEffect(() => {
    if (isOpen) {
      setActiveMode(initialMode);
      setSearchQuery('');
      setSelectedCategory('all');
      setRatingFilter('all');
      setSelectedFood(null);
      setPhotoResult(null);
      setPhotoPreview(null);
      setPhotoError(null);
      setDescribeResult(null);
      setDescribeInput('');
      setDescribeError(null);
      setBarcodeResult(null);
      setBarcodeError(null);
    } else {
      stopBarcodeCamera();
    }
  }, [isOpen, initialMode, stopBarcodeCamera]);

  // Auto-start barcode camera when barcode mode is active
  useEffect(() => {
    if (isOpen && activeMode === 'barcode' && !barcodeResult) {
      startBarcodeCamera();
    } else {
      stopBarcodeCamera();
    }
  }, [isOpen, activeMode, barcodeResult, startBarcodeCamera, stopBarcodeCamera]);

  // --- AI DESCRIBE HANDLER ---
  const handleAiDescribe = async () => {
    const text = describeInput.trim();
    if (!text) return;

    setIsDescribeLoading(true);
    setDescribeError(null);

    try {
      const result = await describeMealDirectOrProxy(text);
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Could not decompose meal description.');
      }

      const data = result.data;
      const dishTitle = data.dish_name || text;
      const rawIngredients = Array.isArray(data.ingredients) ? data.ingredients : [];

      if (Array.isArray(data.discovered_foods) && data.discovered_foods.length > 0) {
        autoRegisterDiscoveredFoods(data.discovered_foods).catch(() => {});
      } else if (rawIngredients.length > 0) {
        autoRegisterDiscoveredFoods(rawIngredients).catch(() => {});
      }

      const rawTags = rawIngredients
        .map((i: any) => (typeof i === 'string' ? i : (i.name || i.title || '')))
        .filter((i: string) => Boolean(i) && !/^(water|salt|pepper|black pepper)$/i.test(i.trim()));

      const evalResult = evaluateFodmapMeal(rawTags, dishTitle);

      setDescribeResult({
        title: dishTitle,
        category: data.meal_category,
        ingredients: rawTags,
        analysis: evalResult,
        notes: data.notes || evalResult.summary || evalResult.clinicalGuidance,
      });
    } catch (err: any) {
      console.warn('FODMAP describe error:', err);
      setDescribeError(err?.message || 'Could not analyze description. Please try again.');
    } finally {
      setIsDescribeLoading(false);
    }
  };

  // --- BARCODE PHOTO UPLOAD HANDLER ---
  const handleBarcodePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsBarcodeLoading(true);
    setBarcodeError(null);

    try {
      // 1. Try multi-angle Barcode decode using shared engine
      const detectedCode = await decodeBarcodeFromPhoto(file);

      if (detectedCode) {
        setBarcodeInput(detectedCode);
        await handleLookupBarcode(detectedCode);
        return;
      }

      // 2. Vision OCR fallback for product package / ingredients photo
      const compressed = await compressImage(file, 1024, 0.85);
      const visionResult = await analyzeImageDirectOrProxy(compressed);
      if (visionResult.success) {
        const dishTitle = visionResult.suggested_name || visionResult.dish_name || 'Scanned Packaged Product';
        let rawIngredients: string[] = [];
        if (Array.isArray(visionResult.ingredients_tags) && visionResult.ingredients_tags.length > 0) {
          rawIngredients = visionResult.ingredients_tags
            .map((i: any) => (typeof i === 'string' ? i : (i?.name || '')))
            .filter((i: string) => Boolean(i) && !/^(water|salt|pepper|black pepper)$/i.test(i.trim()));
        } else if (Array.isArray(visionResult.ingredients) && visionResult.ingredients.length > 0) {
          rawIngredients = visionResult.ingredients
            .map((i: any) => (typeof i === 'string' ? i : (i?.name || '')))
            .filter((i: string) => Boolean(i) && !/^(water|salt|pepper|black pepper)$/i.test(i.trim()));
        }

        const evalResult = evaluateFodmapMeal(rawIngredients, dishTitle);
        setBarcodeResult({
          productName: dishTitle,
          brand: visionResult.suggested_category || undefined,
          ingredients: rawIngredients,
          analysis: evalResult,
        });
      } else {
        setBarcodeError('Could not detect barcode or ingredients in photo. Please enter digits manually or try a clearer photo.');
      }
    } catch (err: any) {
      console.warn('Barcode photo error:', err);
      setBarcodeError('Failed to analyze photo. Please try again.');
    } finally {
      setIsBarcodeLoading(false);
      if (e.target) e.target.value = '';
    }
  };

  // --- PHOTO ANALYSIS HANDLER ---
  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsPhotoScanning(true);
      setPhotoError(null);
      const compressed = await compressImage(file, 1000, 0.82);
      setPhotoPreview(compressed);

      const result = await analyzeImageDirectOrProxy(compressed);

      if (result.success) {
        const dishTitle = result.suggested_name || result.dish_name || 'Analyzed Plate';
        let rawIngredients: string[] = [];
        if (Array.isArray(result.ingredients_tags) && result.ingredients_tags.length > 0) {
          rawIngredients = result.ingredients_tags
            .map((i: any) => (typeof i === 'string' ? i : (i?.name || '')))
            .filter((i: string) => Boolean(i) && !/^(water|salt|pepper|black pepper)$/i.test(i.trim()));
        } else if (Array.isArray(result.ingredients) && result.ingredients.length > 0) {
          rawIngredients = result.ingredients
            .map((i: any) => (typeof i === 'string' ? i : (i?.name || '')))
            .filter((i: string) => Boolean(i) && !/^(water|salt|pepper|black pepper)$/i.test(i.trim()));
        }

        // Auto-register discovered foods
        if (Array.isArray(result.discovered_foods) && result.discovered_foods.length > 0) {
          autoRegisterDiscoveredFoods(result.discovered_foods).catch(() => {});
        }

        const mealAnalysis = evaluateFodmapMeal(rawIngredients, dishTitle);

        setPhotoResult({
          title: dishTitle,
          category: result.suggested_category,
          ingredients: rawIngredients,
          analysis: mealAnalysis,
          notes: result.notes || mealAnalysis.summary || mealAnalysis.clinicalGuidance,
        });
      } else {
        setPhotoError(result.error || 'Could not analyze photo. Please try a clearer image.');
      }
    } catch (err: any) {
      console.warn('Photo scan error:', err);
      setPhotoError('Photo analysis failed. Please try again.');
    } finally {
      setIsPhotoScanning(false);
      if (e.target) e.target.value = '';
    }
  };

  // --- BUILDER ACTIONS ---
  const handleAddFoodToBuilder = (food: FodmapFoodItem) => {
    const existing = builderIngredients.find((i) => i.name.toLowerCase() === food.name.toLowerCase());
    if (existing) return;

    const realistic = getRealisticCulinaryServing(food.name, food);
    const rawGrams = realistic.grams || food.servings?.low?.grams || food.safeGrams || 50;
    const defaultGrams = rawGrams >= 400 ? (realistic.grams && realistic.grams < 400 ? realistic.grams : 100) : rawGrams;

    const newItem = {
      id: `ing-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: food.name,
      grams: defaultGrams,
      rating: food.rating,
      primaryCulprit: food.primaryCulprit,
      swapSuggestion: food.swapSuggestion,
      safeLimitTitle: food.servings?.low?.title || (food.safeGrams ? `Safe: ${food.safeGrams}g` : realistic.servingLimitNote),
    };

    setBuilderIngredients((prev) => {
      const updated = [...prev, newItem];
      if (builderTitle === 'My Custom Plate' || builderTitle === 'My Custom Recipe' || !builderTitle.trim()) {
        const auto = synthesizeDishTitle(updated);
        if (auto) setBuilderTitle(auto);
      }
      return updated;
    });
    setBuilderSearchQuery('');
  };

  const handleAddMultipleToBuilder = (ingredients: string[], evalResult?: FodmapAnalysisResult) => {
    const newItems = ingredients.map((ing) => {
      const matchedEval = evalResult?.detectedIngredients.find(
        (d) => d.raw.toLowerCase() === ing.toLowerCase()
      );
      const matchedFood = matchedEval?.matchedFood || matchIngredientToFodmap(ing).matchedFood;
      const realistic = getRealisticCulinaryServing(ing, matchedFood);
      const rawGrams = realistic.grams || matchedFood?.servings?.low?.grams || matchedFood?.safeGrams || 50;
      const defaultGrams = rawGrams >= 400 ? (realistic.grams && realistic.grams < 400 ? realistic.grams : 100) : rawGrams;

      return {
        id: `ing-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        name: ing,
        grams: defaultGrams,
        rating: matchedEval?.rating || matchedFood?.rating || 'low',
        primaryCulprit: matchedEval?.primaryCulprit || matchedFood?.primaryCulprit,
        swapSuggestion: matchedEval?.swapSuggestion || matchedFood?.swapSuggestion,
        safeLimitTitle: matchedFood?.servings?.low?.title || (matchedFood?.safeGrams ? `Safe: ${matchedFood.safeGrams}g` : realistic.servingLimitNote),
      };
    });

    setBuilderIngredients((prev) => {
      const existingNames = new Set(prev.map((p) => p.name.toLowerCase()));
      const filteredNew = newItems.filter((item) => !existingNames.has(item.name.toLowerCase()));
      const updated = [...prev, ...filteredNew];
      if (builderTitle === 'My Custom Plate' || builderTitle === 'My Custom Recipe' || !builderTitle.trim()) {
        const auto = synthesizeDishTitle(updated);
        if (auto) setBuilderTitle(auto);
      }
      return updated;
    });
    setActiveMode('builder');
  };

  const handleSwapBuilderItem = (index: number) => {
    const item = builderIngredients[index];
    if (!item) return;

    const conversion = convertMealToLowFodmap([item.name], item.name);
    if (conversion.newIngredients && conversion.newIngredients.length > 0) {
      const swappedName = conversion.newIngredients[0];
      const match = matchIngredientToFodmap(swappedName);
      const matched = match.matchedFood;

      setBuilderIngredients((prev) => {
        const copy = [...prev];
        copy[index] = {
          ...item,
          name: swappedName,
          grams: matched?.safeGrams || item.grams,
          rating: 'low',
          primaryCulprit: 'None',
          swapSuggestion: undefined,
          safeLimitTitle: matched?.servings?.low?.title || 'Certified Low FODMAP',
        };
        return copy;
      });
    }
  };

  const handleSaveRecipe = () => {
    if (builderIngredients.length === 0) return;
    const newRecipe: SavedRecipe = {
      id: `rec-${Date.now()}`,
      title: builderTitle.trim() || 'Custom Recipe',
      portionSize: 1.0,
      ingredients: builderIngredients.map((i) => ({
        id: i.id,
        name: i.name,
        canonicalName: canonicalizeIngredient(i.name) || i.name,
        amount: i.grams,
        unit: 'g',
        gramsEstimate: i.grams,
        rating: i.rating,
        primaryCulprit: i.primaryCulprit,
      })),
      createdAtIso: new Date().toISOString(),
    };
    localStore.saveRecipe(newRecipe);
    setBuilderSaveSuccess(true);
    setTimeout(() => setBuilderSaveSuccess(false), 3000);
  };

  const handleLogCurrentMeal = (title: string, ingredients: string[]) => {
    if (!onLogMeal || ingredients.length === 0) return;
    onLogMeal({ name: title, ingredients });
    onClose();
  };

  // Helper badge color
  const getRatingBadgeClass = (rating: 'low' | 'moderate' | 'high') => {
    if (rating === 'low') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30';
    if (rating === 'moderate') return 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30';
    return 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white dark:bg-[#14161F] w-full max-w-lg rounded-3xl shadow-2xl border border-slate-200/80 dark:border-zinc-800 flex flex-col max-h-[92vh] overflow-hidden">
        
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-zinc-800/80 flex items-center justify-between shrink-0">
          <div className="flex items-center space-x-2.5">
            <div className="w-10 h-10 rounded-2xl bg-emerald-500 text-white flex items-center justify-center shadow-md shadow-emerald-500/20 shrink-0">
              <ShieldCheck className="w-5 h-5 stroke-[2.5]" />
            </div>
            <div>
              <h2 className="text-base font-black text-slate-950 dark:text-white tracking-tight">
                Low FODMAP Checker
              </h2>
              <p className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
                Official Monash University Standard (1,670 Foods)
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mode Switcher Tabs */}
        <div className="grid grid-cols-5 gap-1 p-2 bg-slate-100/80 dark:bg-zinc-900/90 border-b border-slate-200/60 dark:border-zinc-800 shrink-0">
          <button
            type="button"
            onClick={() => {
              setActiveMode('search');
              setSelectedFood(null);
            }}
            className={`py-2 px-1 rounded-xl text-xs font-bold transition-all flex flex-col items-center gap-1 ${
              activeMode === 'search'
                ? 'bg-white dark:bg-zinc-800 text-emerald-600 dark:text-emerald-400 shadow-2xs'
                : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <Search className="w-4 h-4" />
            <span>Search</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveMode('builder');
              setSelectedFood(null);
            }}
            className={`py-2 px-1 rounded-xl text-xs font-bold transition-all flex flex-col items-center gap-1 ${
              activeMode === 'builder'
                ? 'bg-white dark:bg-zinc-800 text-emerald-600 dark:text-emerald-400 shadow-2xs'
                : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <ChefHat className="w-4 h-4" />
            <span>Builder</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveMode('describe');
              setSelectedFood(null);
            }}
            className={`py-2 px-1 rounded-xl text-xs font-bold transition-all flex flex-col items-center gap-1 ${
              activeMode === 'describe'
                ? 'bg-white dark:bg-zinc-800 text-emerald-600 dark:text-emerald-400 shadow-2xs'
                : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            <span>Describe</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveMode('barcode');
              setSelectedFood(null);
            }}
            className={`py-2 px-1 rounded-xl text-xs font-bold transition-all flex flex-col items-center gap-1 ${
              activeMode === 'barcode'
                ? 'bg-white dark:bg-zinc-800 text-emerald-600 dark:text-emerald-400 shadow-2xs'
                : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <QrCode className="w-4 h-4" />
            <span>Barcode</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveMode('photo');
              setSelectedFood(null);
            }}
            className={`py-2 px-1 rounded-xl text-xs font-bold transition-all flex flex-col items-center gap-1 ${
              activeMode === 'photo'
                ? 'bg-white dark:bg-zinc-800 text-emerald-600 dark:text-emerald-400 shadow-2xs'
                : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <Camera className="w-4 h-4" />
            <span>Photo</span>
          </button>
        </div>

        {/* Scrollable Main Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          
          {/* ========================================================================= */}
          {/* 1. SEARCH & BROWSE MODE                                                   */}
          {/* ========================================================================= */}
          {activeMode === 'search' && !selectedFood && (
            <div className="space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-zinc-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search 1,670 Monash foods (e.g. Oyster mushroom, Garlic, Oats)..."
                  className="w-full h-11 pl-10 pr-9 rounded-2xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-xs font-medium text-slate-900 dark:text-zinc-100 placeholder-slate-400 focus:outline-none focus:border-emerald-500 transition-colors"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-white p-1"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Rating Filter Pills */}
              <div className="flex items-center space-x-1.5 overflow-x-auto pb-1">
                {(['all', 'low', 'moderate', 'high'] as RatingFilter[]).map((rf) => (
                  <button
                    key={rf}
                    type="button"
                    onClick={() => setRatingFilter(rf)}
                    className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase transition-all shrink-0 ${
                      ratingFilter === rf
                        ? rf === 'low'
                          ? 'bg-emerald-500 text-white shadow-xs'
                          : rf === 'moderate'
                          ? 'bg-amber-500 text-white shadow-xs'
                          : rf === 'high'
                          ? 'bg-rose-500 text-white shadow-xs'
                          : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-xs'
                        : 'bg-slate-100 dark:bg-zinc-800/80 text-slate-600 dark:text-zinc-400 hover:bg-slate-200'
                    }`}
                  >
                    {rf === 'all' ? 'All Ratings' : rf}
                  </button>
                ))}
              </div>

              {/* Category Chips Carousel */}
              <div className="flex items-center space-x-1.5 overflow-x-auto pb-1">
                <button
                  type="button"
                  onClick={() => setSelectedCategory('all')}
                  className={`px-3 py-1 rounded-xl text-xs font-bold transition-all shrink-0 ${
                    selectedCategory === 'all'
                      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30'
                      : 'bg-slate-50 dark:bg-zinc-900 text-slate-600 dark:text-zinc-400 border border-slate-200/60 dark:border-zinc-800 hover:bg-slate-100'
                  }`}
                >
                  All Categories
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-3 py-1 rounded-xl text-xs font-bold transition-all shrink-0 flex items-center space-x-1.5 ${
                      selectedCategory === cat.id
                        ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30'
                        : 'bg-slate-50 dark:bg-zinc-900 text-slate-600 dark:text-zinc-400 border border-slate-200/60 dark:border-zinc-800 hover:bg-slate-100'
                    }`}
                  >
                    <span>{cat.name}</span>
                    <span className="text-[10px] opacity-60">({cat.count})</span>
                  </button>
                ))}
              </div>

              {/* Food List */}
              <div className="space-y-2">
                {filteredFoods.map((food) => (
                  <button
                    key={food.id}
                    type="button"
                    onClick={() => setSelectedFood(food)}
                    className="w-full p-3 rounded-2xl bg-slate-50 hover:bg-slate-100/80 dark:bg-zinc-900 dark:hover:bg-zinc-800/80 border border-slate-200/60 dark:border-zinc-800/80 text-left transition-all flex items-center justify-between group active:scale-[0.99]"
                  >
                    <div className="truncate pr-3">
                      <div className="text-xs font-bold text-slate-900 dark:text-zinc-100 truncate">
                        {food.name}
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-zinc-400 truncate mt-0.5">
                        {food.category} • {food.servings?.low?.title || (food.safeGrams ? `Safe: ${food.safeGrams}g` : 'Standard')}
                        {food.primaryCulprit && food.primaryCulprit !== 'None' ? ` • ${food.primaryCulprit}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 shrink-0">
                      <span className={`text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full border ${getRatingBadgeClass(food.rating)}`}>
                        {food.rating}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </button>
                ))}

                {filteredFoods.length === 0 && (
                  <div className="p-8 text-center bg-slate-50 dark:bg-zinc-900/60 rounded-3xl border border-dashed border-slate-200 dark:border-zinc-800">
                    <Search className="w-8 h-8 text-slate-400 mx-auto mb-2 opacity-50" />
                    <p className="text-xs font-bold text-slate-700 dark:text-zinc-300">
                      No foods found matching "{searchQuery}"
                    </p>
                    <p className="text-[11px] text-slate-500 mt-1">
                      Try searching with generic terms like "rice", "banana", "cheese", or "mushroom".
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* FOOD DETAIL VIEW                                                          */}
          {/* ========================================================================= */}
          {activeMode === 'search' && selectedFood && (
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => setSelectedFood(null)}
                className="flex items-center space-x-1.5 text-xs font-bold text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Foods List</span>
              </button>

              {/* Food Rating Banner */}
              <div
                className={`p-5 rounded-3xl border ${
                  selectedFood.rating === 'low'
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : selectedFood.rating === 'moderate'
                    ? 'bg-amber-500/10 border-amber-500/30'
                    : 'bg-rose-500/10 border-rose-500/30'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-black uppercase px-3 py-1 rounded-full border ${getRatingBadgeClass(selectedFood.rating)}`}>
                    {selectedFood.rating === 'low' ? '🟢 Low FODMAP (Safe)' : selectedFood.rating === 'moderate' ? '🟡 Moderate (Caution)' : '🔴 High FODMAP (Avoid)'}
                  </span>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {selectedFood.category}
                  </span>
                </div>

                <h3 className="text-lg font-black text-slate-950 dark:text-white mt-3">
                  {selectedFood.name}
                </h3>

                {selectedFood.primaryCulprit && selectedFood.primaryCulprit !== 'None' && (
                  <p className="text-xs font-bold text-rose-600 dark:text-rose-400 mt-1">
                    Primary Culprit Carbohydrate: {selectedFood.primaryCulprit}
                  </p>
                )}

                <div className="mt-3 pt-3 border-t border-slate-200/60 dark:border-zinc-800 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-600 dark:text-zinc-400">
                    Safe Serving Limit:
                  </span>
                  <span className="text-xs font-black text-slate-900 dark:text-white">
                    {selectedFood.servings?.low?.title || (selectedFood.safeGrams ? `${selectedFood.safeGrams}g` : 'Safe in typical meals')}
                  </span>
                </div>
              </div>

              {/* Monash Clinical Guidance */}
              {selectedFood.notes && (
                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-zinc-900 border border-slate-200/80 dark:border-zinc-800">
                  <div className="flex items-center space-x-2 text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                    <BookOpen className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    <span>Monash Research Guidance</span>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-zinc-300 leading-relaxed">
                    {selectedFood.notes}
                  </p>
                </div>
              )}

              {/* Certified Low-FODMAP Swap */}
              {selectedFood.rating !== 'low' && selectedFood.swapSuggestion && (
                <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 space-y-2">
                  <div className="flex items-center space-x-2 text-xs font-black text-emerald-800 dark:text-emerald-400">
                    <Sparkles className="w-4 h-4" />
                    <span>Certified Low-FODMAP Alternative</span>
                  </div>
                  <p className="text-xs text-slate-700 dark:text-zinc-200 font-medium">
                    {selectedFood.swapSuggestion}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    handleAddFoodToBuilder(selectedFood);
                    setActiveMode('builder');
                    setSelectedFood(null);
                  }}
                  className="py-3 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center space-x-1.5 shadow-md shadow-emerald-600/20 active:scale-95 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add to Plate</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    handleLogCurrentMeal(selectedFood.name, [selectedFood.name]);
                  }}
                  className="py-3 px-4 rounded-2xl bg-slate-900 hover:bg-slate-800 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-white font-bold text-xs flex items-center justify-center space-x-1.5 active:scale-95 transition-all"
                >
                  <Utensils className="w-4 h-4" />
                  <span>Log as Meal</span>
                </button>
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* 2. RECIPE & PLATE BUILDER MODE                                            */}
          {/* ========================================================================= */}
          {activeMode === 'builder' && (
            <div className="space-y-4">
              {/* Plate Name Input */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600 dark:text-zinc-400">
                  Meal / Recipe Name
                </label>
                <input
                  type="text"
                  value={builderTitle}
                  onChange={(e) => setBuilderTitle(e.target.value)}
                  placeholder="e.g. Chicken & Oyster Mushroom Risotto"
                  className="w-full h-11 px-3.5 rounded-2xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-xs font-bold text-slate-900 dark:text-zinc-100 placeholder-slate-400 focus:outline-none focus:border-emerald-500 transition-colors"
                />
              </div>

              {/* Overall Plate FODMAP Rating Banner */}
              {builderEvaluation && (
                <div
                  className={`p-4 rounded-2xl border ${
                    builderEvaluation.overallRating === 'low'
                      ? 'bg-emerald-500/10 border-emerald-500/30'
                      : builderEvaluation.overallRating === 'moderate'
                      ? 'bg-amber-500/10 border-amber-500/30'
                      : 'bg-rose-500/10 border-rose-500/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-[11px] font-black uppercase px-2.5 py-0.5 rounded-full border ${getRatingBadgeClass(builderEvaluation.overallRating)}`}>
                      Plate Score: {builderEvaluation.overallRating}
                    </span>
                    <span className="text-xs font-bold text-slate-600 dark:text-zinc-400">
                      {builderIngredients.length} Ingredient{builderIngredients.length === 1 ? '' : 's'}
                    </span>
                  </div>

                  {/* FODMAP Stacking Alert */}
                  {builderEvaluation.hasStacking && (
                    <div className="mt-3 pt-2.5 border-t border-slate-200/60 dark:border-zinc-800 flex items-start space-x-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                      <p className="text-[11px] font-bold text-amber-800 dark:text-amber-300 leading-tight">
                        FODMAP Stacking Detected: Multiple ingredients contain{' '}
                        {builderEvaluation.stackedCulprits.map((s) => s.culprit).join(', ')}. Consider 1-tap swaps below to protect your gut.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Add Ingredients Live Search */}
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-zinc-500" />
                <input
                  type="text"
                  value={builderSearchQuery}
                  onChange={(e) => setBuilderSearchQuery(e.target.value)}
                  placeholder="Type food to add to plate (e.g. Rice, Salmon, Tomato)..."
                  className="w-full h-11 pl-10 pr-9 rounded-2xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-xs font-medium text-slate-900 dark:text-zinc-100 placeholder-slate-400 focus:outline-none focus:border-emerald-500 transition-colors"
                />
                {builderSearchQuery && (
                  <button
                    type="button"
                    onClick={() => setBuilderSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-white p-1"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}

                {/* Dropdown Live Results */}
                {builderSearchResults.length > 0 && (
                  <div className="absolute top-12 left-0 right-0 z-20 bg-white dark:bg-zinc-900 rounded-2xl border border-slate-200 dark:border-zinc-800 shadow-xl max-h-48 overflow-y-auto p-1.5 space-y-1">
                    {builderSearchResults.map((food) => (
                      <button
                        key={food.id}
                        type="button"
                        onClick={() => handleAddFoodToBuilder(food)}
                        className="w-full p-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-zinc-800 text-left transition-colors flex items-center justify-between"
                      >
                        <span className="text-xs font-bold text-slate-900 dark:text-white truncate">
                          {food.name}
                        </span>
                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full border ${getRatingBadgeClass(food.rating)}`}>
                          {food.rating}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Ingredients List */}
              <div className="space-y-2">
                {builderIngredients.map((item, idx) => (
                  <div
                    key={item.id}
                    className="p-3 rounded-2xl bg-slate-50 dark:bg-zinc-900 border border-slate-200/60 dark:border-zinc-800 flex items-center justify-between"
                  >
                    <div className="truncate pr-2">
                      <div className="flex items-center space-x-1.5">
                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${item.rating === 'low' ? 'bg-emerald-500' : item.rating === 'moderate' ? 'bg-amber-500' : 'bg-rose-500'}`} />
                        <span className="text-xs font-bold text-slate-900 dark:text-white truncate">
                          {item.name}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500 dark:text-zinc-400 mt-0.5 pl-4">
                        {item.safeLimitTitle || `${item.grams}g`}
                        {item.primaryCulprit && item.primaryCulprit !== 'None' ? ` • ${item.primaryCulprit}` : ''}
                      </div>
                    </div>

                    <div className="flex items-center space-x-1.5 shrink-0">
                      {item.rating !== 'low' && (
                        <button
                          type="button"
                          onClick={() => handleSwapBuilderItem(idx)}
                          className="px-2.5 py-1 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 text-[10px] font-bold transition-all flex items-center space-x-1"
                        >
                          <Sparkles className="w-3 h-3" />
                          <span>Swap</span>
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setBuilderIngredients((prev) => prev.filter((_, i) => i !== idx))}
                        className="p-1.5 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}

                {builderIngredients.length === 0 && (
                  <div className="p-8 text-center bg-slate-50 dark:bg-zinc-900/60 rounded-3xl border border-dashed border-slate-200 dark:border-zinc-800">
                    <ChefHat className="w-8 h-8 text-slate-400 mx-auto mb-2 opacity-50" />
                    <p className="text-xs font-bold text-slate-700 dark:text-zinc-300">
                      Your plate is empty
                    </p>
                    <p className="text-[11px] text-slate-500 mt-1">
                      Search and add ingredients above to evaluate plate-level FODMAP safety.
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {builderIngredients.length > 0 && (
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <button
                    type="button"
                    onClick={handleSaveRecipe}
                    className="py-3 px-4 rounded-2xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-900 dark:text-white font-bold text-xs flex items-center justify-center space-x-1.5 transition-all"
                  >
                    {builderSaveSuccess ? <Check className="w-4 h-4 text-emerald-500" /> : <BookOpen className="w-4 h-4" />}
                    <span>{builderSaveSuccess ? 'Saved to Library!' : 'Save to Recipes'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleLogCurrentMeal(builderTitle, builderIngredients.map((i) => i.name))}
                    className="py-3 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center space-x-1.5 shadow-md shadow-emerald-600/20 active:scale-95 transition-all"
                  >
                    <Utensils className="w-4 h-4" />
                    <span>Log as Meal</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ========================================================================= */}
          {/* 3. AI DESCRIBE MODE                                                       */}
          {/* ========================================================================= */}
          {activeMode === 'describe' && (
            <div className="space-y-4">
              {!describeResult && !isDescribeLoading && (
                <div className="space-y-3">
                  <div className="p-4 rounded-3xl bg-slate-50 dark:bg-zinc-900 border border-slate-200/80 dark:border-zinc-800 space-y-3">
                    <div className="flex items-center space-x-2">
                      <MessageSquare className="w-4 h-4 text-emerald-500" />
                      <h3 className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white">
                        Describe What You're Eating
                      </h3>
                    </div>

                    <textarea
                      value={describeInput}
                      onChange={(e) => setDescribeInput(e.target.value)}
                      placeholder="e.g. Oatmeal with almond milk, blueberries, and honey, or 2 eggs on sourdough with avocado"
                      rows={3}
                      className="w-full p-3.5 rounded-2xl bg-white dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 text-xs font-medium text-slate-900 dark:text-zinc-100 placeholder-slate-400 focus:outline-none focus:border-emerald-500 transition-colors resize-none"
                    />

                    {/* Quick Suggestions Chips */}
                    <div className="space-y-1.5 pt-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                        Quick Examples:
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          'Chicken risotto with oyster mushrooms and parmesan',
                          'Gluten-free oats with almond milk and blueberries',
                          'Greek salad with cucumber, olives, and feta',
                          'Beef stir-fry with rice noodles and pak choi',
                        ].map((example) => (
                          <button
                            key={example}
                            type="button"
                            onClick={() => setDescribeInput(example)}
                            className="px-2.5 py-1 rounded-xl bg-slate-100 dark:bg-zinc-800 hover:bg-slate-200 dark:hover:bg-zinc-700 text-[11px] font-medium text-slate-700 dark:text-zinc-300 transition-colors text-left"
                          >
                            {example}
                          </button>
                        ))}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleAiDescribe}
                      disabled={isDescribeLoading || !describeInput.trim()}
                      className="w-full py-3.5 rounded-2xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold text-xs flex items-center justify-center space-x-2 shadow-md shadow-emerald-600/20 active:scale-95 transition-all"
                    >
                      <Sparkles className="w-4 h-4" />
                      <span>Analyze FODMAP Safety</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Describe Loading State */}
              {isDescribeLoading && (
                <div className="p-8 text-center bg-slate-50 dark:bg-zinc-900 rounded-3xl border border-slate-200 dark:border-zinc-800 space-y-3">
                  <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mx-auto" />
                  <p className="text-xs font-black text-slate-900 dark:text-white">
                    Decomposing ingredients & evaluating Monash FODMAPs...
                  </p>
                  <p className="text-[11px] text-slate-500">
                    Checking stacking risks, culprits, and safe portion limits.
                  </p>
                </div>
              )}

              {/* Describe Error */}
              {describeError && (
                <div className="p-3.5 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center space-x-2 text-rose-700 dark:text-rose-400 text-xs font-bold">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{describeError}</span>
                </div>
              )}

              {/* Describe Result */}
              {describeResult && !isDescribeLoading && (
                <div className="space-y-3.5 animate-in fade-in duration-200">
                  <div
                    className={`p-4 sm:p-5 rounded-3xl border ${
                      describeResult.analysis.overallRating === 'low'
                        ? 'bg-emerald-500/10 border-emerald-500/30'
                        : describeResult.analysis.overallRating === 'moderate'
                        ? 'bg-amber-500/10 border-amber-500/30'
                        : 'bg-rose-500/10 border-rose-500/30'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-0.5">
                        <span
                          className={`text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full border inline-block ${getRatingBadgeClass(
                            describeResult.analysis.overallRating
                          )}`}
                        >
                          {describeResult.analysis.overallRating === 'low'
                            ? '🟢 Low FODMAP (Safe)'
                            : describeResult.analysis.overallRating === 'moderate'
                            ? '🟡 Moderate FODMAP (Caution)'
                            : '🔴 High FODMAP (Avoid)'}
                        </span>
                        <h3 className="text-base font-black text-slate-950 dark:text-white pt-1">
                          {describeResult.title}
                        </h3>
                        {describeResult.category && (
                          <p className="text-xs font-semibold text-slate-500 dark:text-zinc-400">
                            Category: {describeResult.category}
                          </p>
                        )}
                      </div>
                    </div>

                    {describeResult.notes && (
                      <p className="text-xs text-slate-600 dark:text-zinc-300 mt-2.5 pt-2.5 border-t border-slate-200/60 dark:border-zinc-800 leading-relaxed font-medium">
                        {describeResult.notes}
                      </p>
                    )}

                    {describeResult.analysis.flaggedCulprits.length > 0 && (
                      <div className="mt-2.5 flex flex-wrap gap-1">
                        <span className="text-[11px] font-bold text-rose-600 dark:text-rose-400 mr-1 self-center">
                          Culprits:
                        </span>
                        {describeResult.analysis.flaggedCulprits.map((culprit, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 rounded-lg bg-rose-500/15 border border-rose-500/30 text-rose-700 dark:text-rose-400 text-[10px] font-black"
                          >
                            {culprit}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Ingredient Breakdown */}
                  <div className="space-y-2">
                    <span className="text-xs font-bold text-slate-700 dark:text-zinc-300 block px-1">
                      Identified Ingredients ({describeResult.analysis.detectedIngredients.length})
                    </span>

                    <div className="space-y-1.5 max-h-60 overflow-y-auto pr-0.5">
                      {describeResult.analysis.detectedIngredients.map((item, idx) => (
                        <div
                          key={idx}
                          className="p-3 rounded-2xl bg-slate-50 dark:bg-zinc-900 border border-slate-200/60 dark:border-zinc-800/80 flex items-start justify-between gap-2"
                        >
                          <div className="space-y-0.5 min-w-0 flex-1">
                            <div className="flex items-center space-x-1.5">
                              <span
                                className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                                  item.rating === 'low'
                                    ? 'bg-emerald-500'
                                    : item.rating === 'moderate'
                                    ? 'bg-amber-500'
                                    : 'bg-rose-500'
                                }`}
                              />
                              <span className="text-xs font-bold text-slate-900 dark:text-white truncate">
                                {item.raw}
                              </span>
                            </div>

                            {item.primaryCulprit && item.primaryCulprit !== 'None' && (
                              <p className="text-[10px] font-bold text-rose-600 dark:text-rose-400 pl-4">
                                Culprit: {item.primaryCulprit}
                              </p>
                            )}

                            {item.servingLimitNote && (
                              <p className="text-[10px] text-slate-500 dark:text-zinc-400 pl-4">
                                {item.servingLimitNote}
                              </p>
                            )}

                            {item.swapSuggestion && (
                              <p className="text-[10px] text-emerald-700 dark:text-emerald-400 font-semibold pl-4">
                                Low FODMAP Alternative: {item.swapSuggestion}
                              </p>
                            )}
                          </div>

                          <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full border shrink-0 ${getRatingBadgeClass(item.rating)}`}>
                            {item.rating}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-2 pt-1">
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => handleAddMultipleToBuilder(describeResult.ingredients, describeResult.analysis)}
                        className="py-3 px-4 rounded-2xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-900 dark:text-white font-bold text-xs flex items-center justify-center space-x-1.5 transition-all"
                      >
                        <ChefHat className="w-4 h-4" />
                        <span>Add to Plate</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleLogCurrentMeal(describeResult.title, describeResult.ingredients)}
                        className="py-3 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center space-x-1.5 shadow-md shadow-emerald-600/20 active:scale-95 transition-all"
                      >
                        <Utensils className="w-4 h-4" />
                        <span>Log as Meal</span>
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setDescribeResult(null);
                        setDescribeError(null);
                      }}
                      className="w-full py-2.5 rounded-2xl bg-slate-50 dark:bg-zinc-900 hover:bg-slate-100 dark:hover:bg-zinc-800 border border-slate-200/80 dark:border-zinc-800 text-xs font-bold text-slate-700 dark:text-zinc-300 flex items-center justify-center space-x-1.5 transition-colors"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>Describe Another Meal</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ========================================================================= */}
          {/* 4. PHOTO SCAN MODE                                                        */}
          {/* ========================================================================= */}
          {activeMode === 'photo' && (
            <div className="space-y-4">
              <input
                ref={photoCameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handlePhotoSelect}
              />
              <input
                ref={photoGalleryInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoSelect}
              />

              {!photoResult && !isPhotoScanning && (
                <div className="space-y-3 text-center py-4">
                  <div className="w-16 h-16 rounded-3xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto shadow-inner">
                    <Camera className="w-8 h-8" />
                  </div>
                  <h3 className="text-sm font-black text-slate-950 dark:text-white">
                    Direct AI Plate & Packaging Scan
                  </h3>
                  <p className="text-xs text-slate-600 dark:text-zinc-400 max-w-xs mx-auto">
                    Take a photo of your plate, meal, or ingredient packaging for instant Monash FODMAP scoring.
                  </p>

                  <div className="grid grid-cols-2 gap-2.5 pt-2 max-w-xs mx-auto">
                    <button
                      type="button"
                      onClick={() => photoCameraInputRef.current?.click()}
                      className="py-3 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center space-x-1.5 shadow-md shadow-emerald-600/20 active:scale-95 transition-all"
                    >
                      <Camera className="w-4 h-4" />
                      <span>Take Photo</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => photoGalleryInputRef.current?.click()}
                      className="py-3 px-4 rounded-2xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-900 dark:text-white font-bold text-xs flex items-center justify-center space-x-1.5 active:scale-95 transition-all"
                    >
                      <Upload className="w-4 h-4" />
                      <span>Upload Photo</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Scanning Loader */}
              {isPhotoScanning && (
                <div className="p-8 text-center bg-slate-50 dark:bg-zinc-900 rounded-3xl border border-slate-200 dark:border-zinc-800 space-y-3">
                  <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mx-auto" />
                  <p className="text-xs font-black text-slate-900 dark:text-white">
                    Analyzing photo & evaluating FODMAP safety...
                  </p>
                  <p className="text-[11px] text-slate-500">
                    Recognizing ingredients and calculating Monash safety ratings.
                  </p>
                </div>
              )}

              {/* Photo Error */}
              {photoError && (
                <div className="p-3.5 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center space-x-2 text-rose-700 dark:text-rose-400 text-xs font-bold">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{photoError}</span>
                </div>
              )}

              {/* Photo Scan Result */}
              {photoResult && (
                <div className="space-y-3">
                  {photoPreview && (
                    <div className="w-full h-44 rounded-2xl overflow-hidden border border-slate-200 dark:border-zinc-800 relative bg-black">
                      <img src={photoPreview} alt="Scanned Meal" className="w-full h-full object-cover" />
                      <div className="absolute top-2 right-2">
                        <span className={`text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full border shadow-md ${getRatingBadgeClass(photoResult.analysis.overallRating)}`}>
                          {photoResult.analysis.overallRating === 'low' ? '🟢 Low FODMAP' : photoResult.analysis.overallRating === 'moderate' ? '🟡 Moderate' : '🔴 High FODMAP'}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="p-4 rounded-2xl bg-slate-50 dark:bg-zinc-900 border border-slate-200/80 dark:border-zinc-800 space-y-2">
                    <h3 className="text-sm font-black text-slate-950 dark:text-white">
                      {photoResult.title}
                    </h3>
                    <p className="text-xs text-slate-600 dark:text-zinc-300">
                      {photoResult.notes}
                    </p>

                    <div className="pt-2 border-t border-slate-200/60 dark:border-zinc-800">
                      <span className="text-[11px] font-bold text-slate-500 block mb-1.5">
                        Identified Ingredients ({photoResult.ingredients.length}):
                      </span>
                      <div className="space-y-1.5">
                        {photoResult.analysis.detectedIngredients.map((item, i) => (
                          <div
                            key={i}
                            className="p-2.5 rounded-xl bg-white dark:bg-zinc-800 border border-slate-200/60 dark:border-zinc-700 flex items-center justify-between"
                          >
                            <div className="truncate pr-2">
                              <span className="text-xs font-bold text-slate-900 dark:text-white truncate">
                                {item.raw}
                              </span>
                              {item.primaryCulprit && item.primaryCulprit !== 'None' && (
                                <span className="text-[10px] text-rose-500 font-semibold block">
                                  {item.primaryCulprit}
                                </span>
                              )}
                            </div>
                            <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full border shrink-0 ${getRatingBadgeClass(item.rating)}`}>
                              {item.rating}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setPhotoResult(null);
                        setPhotoPreview(null);
                      }}
                      className="py-3 px-4 rounded-2xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-900 dark:text-white font-bold text-xs flex items-center justify-center space-x-1.5 transition-all"
                    >
                      <RotateCcw className="w-4 h-4" />
                      <span>Retake</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleLogCurrentMeal(photoResult.title, photoResult.ingredients)}
                      className="py-3 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center space-x-1.5 shadow-md shadow-emerald-600/20 active:scale-95 transition-all"
                    >
                      <Utensils className="w-4 h-4" />
                      <span>Log as Meal</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ========================================================================= */}
          {/* 4. BARCODE SCANNER MODE (STANDALONE FODMAP CHECKER)                       */}
          {/* ========================================================================= */}
          {activeMode === 'barcode' && (
            <div className="space-y-4">
              <input
                ref={barcodePhotoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleBarcodePhotoSelect}
              />

              {/* View 1: FODMAP Analysis Result for Scanned Barcode Product */}
              {barcodeResult ? (
                <div className="space-y-3.5 animate-in fade-in duration-200">
                  {/* Product Header & Rating Card */}
                  <div
                    className={`p-4 sm:p-5 rounded-3xl border ${
                      barcodeResult.analysis.overallRating === 'low'
                        ? 'bg-emerald-500/10 border-emerald-500/30'
                        : barcodeResult.analysis.overallRating === 'moderate'
                        ? 'bg-amber-500/10 border-amber-500/30'
                        : 'bg-rose-500/10 border-rose-500/30'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-0.5">
                        <span className={`text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full border inline-block ${getRatingBadgeClass(barcodeResult.analysis.overallRating)}`}>
                          {barcodeResult.analysis.overallRating === 'low'
                            ? '🟢 Low FODMAP (Safe)'
                            : barcodeResult.analysis.overallRating === 'moderate'
                            ? '🟡 Moderate FODMAP (Caution)'
                            : '🔴 High FODMAP (Avoid)'}
                        </span>
                        <h3 className="text-base font-black text-slate-950 dark:text-white pt-1">
                          {barcodeResult.productName}
                        </h3>
                        {barcodeResult.brand && (
                          <p className="text-xs font-semibold text-slate-500 dark:text-zinc-400">
                            Brand: {barcodeResult.brand}
                          </p>
                        )}
                      </div>

                      {barcodeResult.imageUrl && (
                        <img
                          src={barcodeResult.imageUrl}
                          alt={barcodeResult.productName}
                          className="w-14 h-14 object-cover rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white shrink-0"
                        />
                      )}
                    </div>

                    {/* Monash Guidance Summary */}
                    {barcodeResult.analysis.summary && (
                      <p className="text-xs text-slate-600 dark:text-zinc-300 mt-2.5 pt-2.5 border-t border-slate-200/60 dark:border-zinc-800 leading-relaxed font-medium">
                        {barcodeResult.analysis.summary}
                      </p>
                    )}

                    {/* Flagged Culprits Tags */}
                    {barcodeResult.analysis.flaggedCulprits.length > 0 && (
                      <div className="mt-2.5 flex flex-wrap gap-1">
                        <span className="text-[11px] font-bold text-rose-600 dark:text-rose-400 mr-1 self-center">
                          Culprits:
                        </span>
                        {barcodeResult.analysis.flaggedCulprits.map((culprit, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 rounded-lg bg-rose-500/15 border border-rose-500/30 text-rose-700 dark:text-rose-400 text-[10px] font-black"
                          >
                            {culprit}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Ingredient-by-Ingredient Traffic Light Breakdown */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between px-1">
                      <span className="text-xs font-bold text-slate-700 dark:text-zinc-300">
                        Ingredient FODMAP Ratings ({barcodeResult.analysis.detectedIngredients.length})
                      </span>
                      <span className="text-[11px] text-slate-400 font-medium">
                        Monash Lab Tested
                      </span>
                    </div>

                    <div className="space-y-1.5 max-h-60 overflow-y-auto pr-0.5">
                      {barcodeResult.analysis.detectedIngredients.map((item, idx) => (
                        <div
                          key={idx}
                          className="p-3 rounded-2xl bg-slate-50 dark:bg-zinc-900 border border-slate-200/60 dark:border-zinc-800/80 flex items-start justify-between gap-2"
                        >
                          <div className="space-y-0.5 min-w-0 flex-1">
                            <div className="flex items-center space-x-1.5">
                              <span
                                className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                                  item.rating === 'low'
                                    ? 'bg-emerald-500'
                                    : item.rating === 'moderate'
                                    ? 'bg-amber-500'
                                    : 'bg-rose-500'
                                }`}
                              />
                              <span className="text-xs font-bold text-slate-900 dark:text-white truncate">
                                {item.raw}
                              </span>
                            </div>

                            {item.primaryCulprit && item.primaryCulprit !== 'None' && (
                              <p className="text-[10px] font-bold text-rose-600 dark:text-rose-400 pl-4">
                                Culprit: {item.primaryCulprit}
                              </p>
                            )}

                            {item.servingLimitNote && (
                              <p className="text-[10px] text-slate-500 dark:text-zinc-400 pl-4">
                                {item.servingLimitNote}
                              </p>
                            )}

                            {item.swapSuggestion && (
                              <p className="text-[10px] text-emerald-700 dark:text-emerald-400 font-semibold pl-4">
                                Low FODMAP Alternative: {item.swapSuggestion}
                              </p>
                            )}
                          </div>

                          <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full border shrink-0 ${getRatingBadgeClass(item.rating)}`}>
                            {item.rating}
                          </span>
                        </div>
                      ))}

                      {barcodeResult.analysis.detectedIngredients.length === 0 && (
                        <div className="p-4 text-center text-xs text-slate-500">
                          No specific ingredients extracted from barcode database.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions for Checked Product */}
                  <div className="space-y-2 pt-1">
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => handleAddMultipleToBuilder(barcodeResult.ingredients, barcodeResult.analysis)}
                        className="py-3 px-4 rounded-2xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-900 dark:text-white font-bold text-xs flex items-center justify-center space-x-1.5 transition-all"
                      >
                        <ChefHat className="w-4 h-4" />
                        <span>Add to Plate</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleLogCurrentMeal(barcodeResult.productName, barcodeResult.ingredients)}
                        className="py-3 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center space-x-1.5 shadow-md shadow-emerald-600/20 active:scale-95 transition-all"
                      >
                        <Utensils className="w-4 h-4" />
                        <span>Log as Meal</span>
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setBarcodeResult(null);
                        setBarcodeError(null);
                        startBarcodeCamera();
                      }}
                      className="w-full py-2.5 rounded-2xl bg-slate-50 dark:bg-zinc-900 hover:bg-slate-100 dark:hover:bg-zinc-800 border border-slate-200/80 dark:border-zinc-800 text-xs font-bold text-slate-700 dark:text-zinc-300 flex items-center justify-center space-x-1.5 transition-colors"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>Scan Another Barcode</span>
                    </button>
                  </div>
                </div>
              ) : (
                /* View 2: Camera Viewfinder & Barcode Options */
                <div className="space-y-4">
                  {isBarcodeCameraActive ? (
                    <div className="space-y-2.5">
                      <div className="relative w-full aspect-[4/3] max-h-[260px] bg-black rounded-3xl overflow-hidden border border-slate-200 dark:border-zinc-800 flex items-center justify-center shadow-inner">
                        <video
                          ref={videoRef}
                          className="w-full h-full object-cover"
                          playsInline
                          muted
                          autoPlay
                        />
                        {/* 360° Reticle overlay */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none p-4">
                          <div className="w-56 h-40 border-2 border-emerald-400/80 rounded-2xl relative shadow-lg">
                            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-0.5 bg-emerald-400 animate-pulse shadow-sm" />
                            <div className="absolute -top-7 inset-x-0 text-center text-[10px] font-black uppercase tracking-wider text-white bg-black/70 backdrop-blur-xs px-2.5 py-0.5 rounded-full mx-auto w-max border border-white/20">
                              360° Omnidirectional
                            </div>
                          </div>
                        </div>

                        {/* Top controls: Torch */}
                        {torchAvailable && (
                          <div className="absolute top-3 right-3 flex items-center space-x-1.5 z-10">
                            <button
                              type="button"
                              onClick={toggleTorch}
                              className={`p-2.5 rounded-xl backdrop-blur-md border transition-all ${
                                isTorchOn
                                  ? 'bg-amber-500 text-white border-amber-400 shadow-md shadow-amber-500/30'
                                  : 'bg-black/50 text-white/80 border-white/20 hover:bg-black/70'
                              }`}
                              title={isTorchOn ? 'Turn Torch Off' : 'Turn Torch On'}
                            >
                              <Flashlight className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={stopBarcodeCamera}
                        className="w-full py-2.5 rounded-2xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-xs font-bold text-slate-700 dark:text-zinc-300 transition-colors flex items-center justify-center space-x-1.5"
                      >
                        <StopCircle className="w-4 h-4" />
                        <span>Stop Camera</span>
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setBarcodeError(null);
                          startBarcodeCamera();
                        }}
                        className="py-3.5 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center space-x-2 shadow-md shadow-emerald-600/20 active:scale-95 transition-all"
                      >
                        <QrCode className="w-4 h-4" />
                        <span>Scan with Camera</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => barcodePhotoInputRef.current?.click()}
                        className="py-3.5 px-4 rounded-2xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-900 dark:text-white font-bold text-xs flex items-center justify-center space-x-2 active:scale-95 transition-all"
                      >
                        <Upload className="w-4 h-4" />
                        <span>Upload Photo</span>
                      </button>
                    </div>
                  )}

                  {barcodeCameraError && (
                    <div className="p-3.5 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center space-x-2 text-rose-700 dark:text-rose-400 text-xs font-bold">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>{barcodeCameraError}</span>
                    </div>
                  )}

                  {/* Manual Barcode Input */}
                  <div className="space-y-1.5 pt-1">
                    <label className="text-xs font-bold text-slate-600 dark:text-zinc-400">
                      Or type barcode digits manually
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={barcodeInput}
                        onChange={(e) => setBarcodeInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleLookupBarcode(barcodeInput);
                          }
                        }}
                        placeholder="e.g. 5000128000123"
                        className="flex-1 h-11 px-3.5 rounded-2xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-xs font-medium text-slate-900 dark:text-zinc-100 placeholder-slate-400 focus:outline-none focus:border-emerald-500 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => handleLookupBarcode(barcodeInput)}
                        disabled={isBarcodeLoading || !barcodeInput.trim()}
                        className="px-4 rounded-2xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold text-xs hover:opacity-90 disabled:opacity-50 transition-opacity"
                      >
                        {isBarcodeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Lookup'}
                      </button>
                    </div>
                  </div>

                  {/* Loading State */}
                  {isBarcodeLoading && (
                    <div className="p-6 text-center bg-slate-50 dark:bg-zinc-900 rounded-3xl border border-slate-200 dark:border-zinc-800 space-y-2">
                      <Loader2 className="w-6 h-6 text-emerald-500 animate-spin mx-auto" />
                      <p className="text-xs font-bold text-slate-800 dark:text-zinc-200">
                        Analyzing product & ingredients with Monash FODMAP rules...
                      </p>
                    </div>
                  )}

                  {/* Barcode Error */}
                  {barcodeError && (
                    <div className="p-3.5 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center space-x-2 text-rose-700 dark:text-rose-400 text-xs font-bold">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>{barcodeError}</span>
                    </div>
                  )}

                  {/* Monash Barcode Explainer */}
                  <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-zinc-900/60 border border-slate-200/60 dark:border-zinc-800/80 space-y-1">
                    <p className="text-[11px] font-bold text-slate-700 dark:text-zinc-300">
                      Standalone Monash FODMAP Ingredient Checker
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-zinc-400 leading-relaxed">
                      Scanning evaluates every packaged ingredient against certified Monash thresholds. You can inspect whether the item is Low or High FODMAP, view culprits, and choose if and when to log it.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
